import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CodeBlock } from "./CodeBlock";

interface MarkdownRendererProps {
	content: string;
}

export function MarkdownRenderer({ content }: MarkdownRendererProps) {
	return (
		<ReactMarkdown
			remarkPlugins={[remarkGfm]}
			components={{
				p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
				ul: ({ children }) => <ul className="mb-3 list-disc pl-5 space-y-1">{children}</ul>,
				ol: ({ children }) => <ol className="mb-3 list-decimal pl-5 space-y-1">{children}</ol>,
				li: ({ children }) => <li className="leading-relaxed">{children}</li>,
				h1: ({ children }) => <h1 className="text-xl font-bold mb-3 mt-4">{children}</h1>,
				h2: ({ children }) => <h2 className="text-lg font-semibold mb-2 mt-3">{children}</h2>,
				h3: ({ children }) => <h3 className="text-base font-semibold mb-2 mt-3">{children}</h3>,
				code: ({ children, className }) => {
					const match = /language-(\w+)/.exec(className || "");
					const isInline = !className;
					if (isInline) {
						return (
							<code className="px-1.5 py-0.5 rounded bg-secondary text-sm font-mono">
								{children}
							</code>
						);
					}
					return <CodeBlock language={match?.[1]}>{String(children).replace(/\n$/, "")}</CodeBlock>;
				},
				pre: ({ children }) => <>{children}</>,
				blockquote: ({ children }) => (
					<blockquote className="border-l-4 border-primary pl-4 italic my-3 text-muted-foreground">
						{children}
					</blockquote>
				),
				a: ({ children, href }) => (
					<a
						href={href}
						target="_blank"
						rel="noopener noreferrer"
						className="text-primary underline underline-offset-2 hover:text-primary/80"
					>
						{children}
					</a>
				),
				table: ({ children }) => (
					<div className="overflow-x-auto my-3">
						<table className="w-full text-sm border-collapse">{children}</table>
					</div>
				),
				thead: ({ children }) => <thead className="bg-secondary">{children}</thead>,
				th: ({ children }) => (
					<th className="px-3 py-2 text-left font-semibold border border-border">{children}</th>
				),
				td: ({ children }) => <td className="px-3 py-2 border border-border">{children}</td>,
				hr: () => <hr className="my-4 border-border" />,
			}}
		>
			{content}
		</ReactMarkdown>
	);
}
